import type { PublicConfig } from "@shared/api"

/**
 * Config del browser, traída en RUNTIME de GET /api/config.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUÉ RUNTIME Y NO `VITE_*` BAKEADO EN EL BUILD:
 *
 *  1. EL ARGUMENTO DECISIVO — la deriva. Bakear forkearía SUPABASE_URL en una segunda
 *     variable. Si alguna vez apuntan a proyectos distintos se obtiene el peor modo de
 *     falla posible: el login FUNCIONA (contra el proyecto A) y después TODAS las
 *     llamadas a la API dan 401 (el token se verifica contra el B), sin nada en los
 *     logs que lo explique. Así, la URL sale de la MISMA process.env.SUPABASE_URL que
 *     usa el verificador de tokens: la deriva es estructuralmente imposible.
 *  2. Rotar la anon key es cambiar una env var + restart, no rebuild + redeploy.
 *  3. El artefacto del build queda independiente del entorno (mismo bundle local y
 *     prod), y no hay NADA nuevo que configurar en el build de DigitalOcean.
 *  4. Un build sin .env poblado emitiría un bundle apuntando a la nada. Acá el
 *     servidor valida y responde 503, y el SPA muestra una pantalla explícita.
 *
 * Costo: un round-trip same-origin (~10 ms) antes del primer render, y un estado de
 * carga — que de todos modos hace falta para el getSession() inicial.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export class ConfigUnavailable extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message)
    this.name = "ConfigUnavailable"
  }
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  let res: Response
  try {
    res = await fetch("/api/config", { credentials: "omit", cache: "no-store" })
  } catch {
    throw new ConfigUnavailable(0, "network", "No se pudo contactar al servidor.")
  }

  if (!res.ok) {
    let code = "config_error"
    let message = `El servidor respondió ${res.status}.`
    try {
      const body = (await res.json()) as { error?: string; message?: string }
      if (body.error) code = body.error
      if (code === "dashboard_not_configured") {
        message =
          "El servidor no está configurado: falta SUPABASE_ANON_KEY. " +
          "Agregala en las variables de entorno de la app y reiniciá."
      } else if (body.message) {
        message = body.message
      }
    } catch {
      // Un 502 de proxy devuelve HTML: se queda el mensaje genérico.
    }
    throw new ConfigUnavailable(res.status, code, message)
  }

  const cfg = (await res.json()) as Partial<PublicConfig>
  if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) {
    throw new ConfigUnavailable(
      200,
      "config_incomplete",
      "La configuración recibida está incompleta."
    )
  }
  return { supabaseUrl: cfg.supabaseUrl, supabaseAnonKey: cfg.supabaseAnonKey }
}
